import { type ReactNode } from 'react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { LoadingDots } from './loading-dots';
import { ContentStream } from '@/components/shared/content-stream';
import { MarkdownRenderer } from '@/components/shared/markdown-renderer';
import { useIaBuildContext } from '@/packages/home/context/ia-builder';
import { homeSections } from '@/packages/home/home-sections';

const finalMessage = 'Sua experiência foi personalizada! 🎉';

interface ExperienceBuilderProps {
  onComplete?: () => void;
  sections?: ReturnType<typeof homeSections>;
}

interface WrappedExperienceBuilderProps {
  isAllSectionsLoaded: boolean;
  children: ReactNode;
  progress: number;
  handleComplete: () => void;
}

const WrappedExperienceBuilder = ({
  isAllSectionsLoaded,
  children,
  progress,
  handleComplete,
}: WrappedExperienceBuilderProps) => {
  return (
    <Stack
      position="fixed"
      top={54}
      right={0}
      left={0}
      zIndex={1000}
      gap={1}
      direction="row"
      alignItems="center"
      justifyContent="center"
      mx={2}
    >
      <Paper
        elevation={2}
        sx={({ shape }) => ({
          p: 3,
          borderRadius: `${shape.borderRadius}px`,
        })}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {!isAllSectionsLoaded && <LoadingDots />}
          {children}
          {!isAllSectionsLoaded && progress > 1 && (
            <Typography variant="body2" color="info.main" fontWeight="bold">
              {progress}%
            </Typography>
          )}
          {isAllSectionsLoaded && (
            <Button
              variant="contained"
              size="small"
              onClick={handleComplete}
            >
              Começar
            </Button>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};

const ExperienceBuilder = ({
  onComplete,
  sections,
}: ExperienceBuilderProps) => {
  const {
    visibleSectionIds,
    isAllSectionsLoaded,
    getMessageForSection,
    messageCompleted,
  } = useIaBuildContext();

  const totalIaSections = sections?.filter((s) => !s.skipIaBuilder).length;
  const progress = Math.round(
    (visibleSectionIds.length / (totalIaSections ?? 0)) * 100
  );

  const currentSectionId = visibleSectionIds[visibleSectionIds.length - 1];
  const currentMessage = getMessageForSection(currentSectionId);

  const handleComplete = () => {
    onComplete?.();
  };

  return (
    <WrappedExperienceBuilder
      isAllSectionsLoaded={isAllSectionsLoaded}
      progress={progress}
      handleComplete={handleComplete}
    >
      <ContentStream
        content={isAllSectionsLoaded ? finalMessage : currentMessage || ''}
        splitBy="character"
        tokenDelayRange={[0, 40]}
        startDelayRange={[0, 200]}
        bypassStream={messageCompleted}
      >
        {({ markdownContent }) => (
          <MarkdownRenderer markdown={markdownContent} />
        )}
      </ContentStream>
    </WrappedExperienceBuilder>
  );
};

const ExperienceBuilderSkeleton = () => {
  return (
    <WrappedExperienceBuilder
      isAllSectionsLoaded={false}
      progress={0}
      handleComplete={() => {}}
    >
      <Typography>🤖 Preparando sua experiência personalizada...</Typography>
    </WrappedExperienceBuilder>
  );
};

export { ExperienceBuilderSkeleton, ExperienceBuilder };
